/**
 * ScopeBadge — persistent topbar indicator that tells the user which scope
 * (Company vs Location) they are currently operating in.
 *
 * Visual contract aligned with `shaka-deck-mockup-example-scope-staff.html`
 * (2026-04-25): company scope is the emphatic, filled ocean pill — to match
 * the company-scope page frame's vertical ocean band — while location scope
 * is the soft sage pill, calmer because it's the default operating mode for
 * most users.
 *
 *   company  → bg-ocean-700  text-white   (filled, high contrast)
 *   location → bg-sage-100   text-sage-700 (soft, low contrast)
 *
 * The component reads the active scope from `useActiveContext` itself rather
 * than taking a prop, so any place that mounts it stays consistent with the
 * rest of the app without wiring.
 */

import { Building2, MapPin } from 'lucide-react';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useTranslation } from '@/core/i18n';
import { cn } from '@/lib/utils';

export interface ScopeBadgeProps {
  /** Optional className forwarded to the outer element. */
  className?: string;
}

export function ScopeBadge({ className }: ScopeBadgeProps) {
  const { t } = useTranslation();
  const { isCompanyContext, isLocationContext } = useActiveContext();

  if (!isCompanyContext && !isLocationContext) {
    return null;
  }

  if (isCompanyContext) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md bg-ocean-700 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-white shadow-sm',
          className,
        )}
        aria-label={t('common.scope.companyAria', 'You are in the Company view')}
      >
        <Building2 className="h-3 w-3" aria-hidden="true" />
        {t('common.scope.company', 'Company')}
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-sage-300 bg-sage-100 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-sage-700',
        className,
      )}
      aria-label={t('common.scope.locationAria', 'You are in a Location view')}
    >
      <MapPin className="h-3 w-3" aria-hidden="true" />
      {t('common.scope.location', 'Location')}
    </span>
  );
}

export default ScopeBadge;
